import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import { ArrowLeft, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "@/api";
import { EmptyRow, Section } from "@/components/manage/Section";
import { PageHeader } from "@/components/manage/PageHeader";
import { SpaceMembersSection } from "@/components/manage/SpaceMembersSection";
import { StatStrip } from "@/components/manage/StatStrip";
import {
  ConnectionChip,
  type DatasetSourceInfo,
  SyncDetail,
} from "@/components/manage/connectors";
import { useActiveOrgId, useIsDemoManage } from "@/components/manage/org-context";

export const Route = createFileRoute("/manage/workspaces/$id")({
  component: WorkspaceDetailPage,
});

interface WorkspaceRow {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  space_id: string | null;
  updated_at: string;
}

interface DatasetSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  file_count: number;
}

function WorkspaceDetailPage() {
  const api = useApi();
  const orgId = useActiveOrgId();
  // Demo sessions: read-only — no reindex, no dataset (un)assignment.
  const isDemo = useIsDemoManage();
  const { id: workspaceId } = useParams({ from: "/manage/workspaces/$id" });
  const qc = useQueryClient();

  const workspace = useQuery({
    queryKey: ["admin-workspace", workspaceId],
    queryFn: () =>
      api
        .request<WorkspaceRow[]>(`/admin/orgs/${orgId}/workspaces`)
        .then(
          (rows) => rows.find((w) => w.id === workspaceId) ?? null,
        ),
  });

  const spaceId = workspace.data?.space_id ?? null;
  const members = useQuery({
    queryKey: ["admin-space-members", spaceId],
    queryFn: () =>
      api.request<{ user_id: string }[]>(`/admin/spaces/${spaceId}/members`),
    enabled: !!spaceId,
  });

  const assignedDatasets = useQuery({
    queryKey: ["admin-workspace-datasets", workspaceId],
    queryFn: () =>
      api.request<DatasetSummary[]>(`/admin/workspaces/${workspaceId}/datasets`),
  });

  // Connector source (icon/name/email) per dataset. Quiet on 404 (feature off).
  const sources = useQuery({
    queryKey: ["connector-sources"],
    queryFn: () => api.request<DatasetSourceInfo[]>(`/connectors/sources`),
    retry: false,
    enabled: !isDemo,
  });
  const sourceByDataset = new Map(
    (sources.data ?? []).map((s) => [s.dataset_id, s] as const),
  );

  const unassignDataset = useMutation({
    mutationFn: (datasetId: string) =>
      api.request<void>(
        `/admin/workspaces/${workspaceId}/datasets/${datasetId}`,
        { method: "DELETE" },
        false,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-workspace-datasets", workspaceId] });
      toast.success("Dataset unassigned");
    },
  });

  // Re-run the document pipeline (parse → chunk + contextualise → embed) for
  // every doc in this workspace — backfills ingestion fixes onto files that
  // were ingested earlier. Processing is async on the data plane.
  const reindex = useMutation({
    mutationFn: () =>
      api.request<{ documents_reindexed: number; skipped: number }>(
        `/admin/workspaces/${workspaceId}/reindex`,
        { method: "POST" },
      ),
    onSuccess: (r) => {
      const n = r?.documents_reindexed ?? 0;
      toast.success(
        n > 0
          ? `Reindexing ${n} document${n === 1 ? "" : "s"}… (processes in the background)`
          : "No documents to reindex",
      );
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Reindex failed");
    },
  });

  if (workspace.isLoading || !workspace.data) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="mt-8 h-40 w-full" />
      </div>
    );
  }

  const ws = workspace.data;
  const memberCount = members.data?.length ?? 0;
  const datasetCount = assignedDatasets.data?.length ?? 0;
  const fileSum =
    assignedDatasets.data?.reduce((acc, d) => acc + d.file_count, 0) ?? 0;

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <Link
        to="/manage/workspaces"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3" /> All workspaces
      </Link>

      <PageHeader
        eyebrow="Workspace"
        tabTitle={[ws.name, "Workspaces"]}
        title={
          <span className="inline-flex items-center gap-3">
            <span className="text-3xl">{ws.icon ?? "📂"}</span>
            {ws.name}
          </span>
        }
        meta={
          <>
            <code className="rounded bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[11px]">
              /{ws.slug}
            </code>
            <span>· Updated {new Date(ws.updated_at).toLocaleDateString()}</span>
          </>
        }
        actions={
          isDemo ? undefined : (
            <Button
              variant="outline"
              onClick={() => reindex.mutate()}
              disabled={reindex.isPending}
              title="Re-run parsing + indexing for every document in this workspace"
            >
              <RefreshCw
                className={`size-4 ${reindex.isPending ? "animate-spin" : ""}`}
              />
              {reindex.isPending ? "Reindexing…" : "Reindex documents"}
            </Button>
          )
        }
      />

      <StatStrip
        items={[
          { label: "Members", value: memberCount, hint: "have access" },
          { label: "Datasets", value: datasetCount, hint: "assigned" },
          { label: "Files", value: fileSum, hint: "across datasets" },
          {
            label: "Slug",
            value: <span className="font-mono text-base">/{ws.slug}</span>,
            hint: "url segment",
          },
        ]}
      />

      {ws.space_id && (
        <SpaceMembersSection
          spaceId={ws.space_id}
          orgId={orgId}
          subjectLabel="workspace"
        />
      )}

      <Section
        title="Assigned datasets"
        description="Datasets visible inside this workspace's canvases. Tabular files (CSV/XLSX) get ingested into DuckDB."
        actions={
          isDemo ? undefined : (
            <AssignDatasetDialog
              workspaceId={workspaceId}
              orgId={orgId}
              existingDatasetIds={(assignedDatasets.data ?? []).map((d) => d.id)}
            />
          )
        }
      >
        {assignedDatasets.isLoading ? (
          <Skeleton className="h-24 w-full rounded-md" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface-soft)]">
                <tr className="text-left">
                  <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Name
                  </th>
                  <th className="w-24 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    Files
                  </th>
                  {!isDemo && (
                    <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Synced from
                    </th>
                  )}
                  {!isDemo && (
                    <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Sync
                    </th>
                  )}
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                {assignedDatasets.data?.length ? (
                  assignedDatasets.data.map((d) => (
                    <tr
                      key={d.id}
                      className="group transition hover:bg-[var(--color-surface)]"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to="/manage/datasets/$id"
                          params={{ id: d.id }}
                          className="inline-flex items-center gap-2 font-medium text-foreground hover:text-[var(--color-primary)]"
                        >
                          <span>{d.icon ?? "📚"}</span>
                          {d.name}
                        </Link>
                        {d.description && (
                          <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                            {d.description}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                        {d.file_count}
                      </td>
                      {!isDemo && (
                        <td className="px-4 py-3">
                          {(() => {
                            const s = sourceByDataset.get(d.id);
                            return s ? (
                              <ConnectionChip
                                provider={s.provider}
                                name={s.display_name}
                                account={s.account_label}
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            );
                          })()}
                        </td>
                      )}
                      {!isDemo && (
                        <td className="px-4 py-3">
                          {(() => {
                            const s = sourceByDataset.get(d.id);
                            return s ? (
                              <SyncDetail source={s} />
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            );
                          })()}
                        </td>
                      )}
                      <td className="px-2 py-2">
                        {!isDemo && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 opacity-0 transition group-hover:opacity-100"
                            onClick={() => unassignDataset.mutate(d.id)}
                            aria-label="Unassign dataset"
                          >
                            <Trash2 className="size-3.5 text-muted-foreground" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow colSpan={isDemo ? 3 : 5}>
                    No datasets assigned to this workspace.
                  </EmptyRow>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function AssignDatasetDialog({
  workspaceId,
  orgId,
  existingDatasetIds,
}: {
  workspaceId: string;
  orgId: string;
  existingDatasetIds: string[];
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [datasetId, setDatasetId] = useState<string>("");

  const datasets = useQuery({
    queryKey: ["admin-datasets", orgId],
    queryFn: () => api.request<DatasetSummary[]>(`/admin/orgs/${orgId}/datasets`),
    enabled: open,
  });

  const available = (datasets.data ?? []).filter(
    (d) => !existingDatasetIds.includes(d.id),
  );

  const assign = useMutation({
    mutationFn: () =>
      api.request<{ workspace_id: string; dataset_id: string }>(
        `/admin/workspaces/${workspaceId}/datasets`,
        {
          method: "POST",
          body: JSON.stringify({ dataset_id: datasetId }),
        },
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["admin-workspace-datasets", workspaceId],
      });
      toast.success("Dataset assigned");
      setOpen(false);
      setDatasetId("");
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? "Assign failed");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button size="sm" variant="outline">
            <Plus className="size-3.5" />
            Assign dataset
          </Button>
        }
      />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign dataset</DialogTitle>
          <DialogDescription>
            Pick a dataset to make available in this workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Select value={datasetId} onValueChange={(v) => setDatasetId(v ?? "")}>
            <SelectTrigger>
              {/* Base UI <Select.Value> renders the raw VALUE (the dataset id)
                  unless given a function child to map it to a label — pass one so
                  the trigger shows the dataset NAME, not its id. */}
              <SelectValue
                placeholder={available.length ? "Pick a dataset" : "All datasets assigned"}
              >
                {(value: string | null) =>
                  value
                    ? ((datasets.data ?? []).find((d) => d.id === value)?.name ?? value)
                    : available.length
                      ? "Pick a dataset"
                      : "All datasets assigned"
                }
              </SelectValue>
            </SelectTrigger>
            {/* Dataset names are long ("Growth Funnel (…)") — detach the popup
                from the narrow trigger width so they aren't clipped. */}
            <SelectContent alignItemWithTrigger={false} className="w-auto min-w-[340px]">
              {available.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button disabled={!datasetId || assign.isPending} onClick={() => assign.mutate()}>
            {assign.isPending ? "Assigning…" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
